import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

export const MultiSelectEditModal = ({
  isOpen,
  onClose,
  title,
  options,
  initialSelected,
  onSave,
  allowOther = false,
  otherLabel = 'Other:',
  otherPlaceholder = 'Other',
  initialOther = '',
  columns = 3,
  maxWidth = 'max-w-3xl',
}: ModalBase & {
  title: string;
  options: Array<{ id: number; name: string; description?: string }>;
  initialSelected: number[];
  // The `otherName` arg is set when `allowOther` is true and the user typed a
  // value into the freeform input. Callers who use the preset-only variant
  // can ignore it.
  onSave: (ids: number[], otherName?: string) => Promise<void>;
  allowOther?: boolean;
  otherLabel?: string;
  otherPlaceholder?: string;
  initialOther?: string;
  columns?: 1 | 2 | 3;
  maxWidth?: string;
}): JSX.Element => {
  const [selected, setSelected] = useState<Set<number>>(new Set(initialSelected));
  const [other, setOther] = useState<string>(initialOther);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialSelected));
      setOther(initialOther);
      setError('');
    }
  }, [isOpen, initialSelected, initialOther]);

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave([...selected], allowOther ? other.trim() : undefined);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // CSS columns keep all checkboxes visible at a glance instead of a scrolling dropdown.
  const colClass =
    columns === 3
      ? 'sm:columns-2 md:columns-3'
      : columns === 2
        ? 'sm:columns-2'
        : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className={`w-full ${maxWidth}`}
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={`${colClass} gap-x-6`}>
        {options.map((option) => (
          <label
            key={option.id}
            className='flex items-start gap-2 py-1 break-inside-avoid'
          >
            <input
              type='checkbox'
              checked={selected.has(option.id)}
              onChange={(): void => toggle(option.id)}
              className='mt-1 flex-shrink-0'
            />
            <span className='min-w-0'>
              <span className='font-medium'>{option.name}</span>
              {option.description && (
                <span className='text-sm block text-neutral-600'>{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {allowOther && (
        <div className='mt-4 flex items-center gap-3'>
          <label htmlFor='other-edit-input' className='font-semibold whitespace-nowrap'>
            {otherLabel}
          </label>
          <input
            id='other-edit-input'
            type='text'
            className='form-control flex-1 border border-neutral-300 rounded px-3 py-2'
            placeholder={otherPlaceholder}
            value={other}
            onChange={(e): void => setOther(e.target.value)}
          />
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};
